import { BusinessEmployee } from "@1games-ru/region-shared/dist/backend/requests/business";
import { EmployeeRole } from "@1games-ru/region-shared/dist/enums/business/employee";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { cn } from "@/core/helpers/cn";
import PersonalDataCard from "@/shared/widgets/PersonalDataCard";
import { EmployeesTab } from "@/domains/business/constants/employeesTab";

type Props = {
  employees: BusinessEmployee[];
  employeesCurrentTab: EmployeesTab;
  onChange: (employee: BusinessEmployee) => void;
  onCall: (id: number) => void;
  onMsg: (id: number) => void;
  onDismiss: (employee: BusinessEmployee) => void;
  className?: string;
  isHideDelete: boolean;
  isHideHandlersId: number;
};

export default function BusinessEmployeesList({
  className,
  employees,
  employeesCurrentTab,
  onChange,
  onCall,
  onMsg,
  onDismiss,
  isHideDelete,
  isHideHandlersId,
}: Props) {
  const { t } = useTranslation("business");

  const getCallHandler = useCallback(
    (id: number) => {
      return isHideHandlersId === id ? null : () => onCall(id);
    },
    [onCall, isHideHandlersId],
  );

  const getMsgHandler = useCallback(
    (id: number) => {
      return isHideHandlersId === id ? null : () => onMsg(id);
    },
    [onMsg, isHideHandlersId],
  );

  const getDismissHandler = useCallback(
    (employee: BusinessEmployee) => {
      return isHideHandlersId === employee.characterId || isHideDelete
        ? null
        : () => onDismiss(employee);
    },
    [onDismiss, isHideDelete, isHideHandlersId],
  );

  const managers = useMemo(() => {
    return employees.filter((item) => item.role === EmployeeRole.Manager);
  }, [employees]);

  const workers = useMemo(() => {
    return employees.filter((item) => item.role === EmployeeRole.Worker);
  }, [employees]);

  const noData = (
    <div
      className={clsx(
        "h-[3.5rem] flex items-center px-5 uppercase text-12 font-bold text-white/50",
        "border border-dashed border-[#1A1A1A] rounded-2xl bg-clip-padding",
      )}
    >
      {t("business.noData")}
    </div>
  );

  const renderEmployeesList = (list: BusinessEmployee[]) => {
    return list.map((item) => (
      <PersonalDataCard
        key={item.characterId}
        name={item.name}
        id={item.characterId}
        isOnline={!!item.playerMpId}
        onClick={() => onChange(item)}
        onCall={getCallHandler(item.characterId)}
        onMsg={getMsgHandler(item.characterId)}
        onDismiss={getDismissHandler(item)}
      />
    ));
  };

  return (
    <div className={cn("flex flex-col gap-1.5 pt-4", className)}>
      {!employeesCurrentTab ||
        (employeesCurrentTab === EmployeesTab.All && (
          <>
            {managers.length > 0 && (
              <p className="mb-2 text-12 font-bold text-white/50 leading-none uppercase">
                {t("business.employees.tabs.managers")}:
              </p>
            )}
            {managers.length > 0 && renderEmployeesList(managers)}
            {workers.length > 0 && (
              <p
                className={clsx(
                  "mb-2 text-12 font-bold text-white/50 leading-none uppercase",
                  managers.length > 0 && "mt-5",
                )}
              >
                {t("business.employees.tabs.serviceStaff")}:
              </p>
            )}
            {workers.length > 0 && renderEmployeesList(workers)}
            {managers.length === 0 && workers.length === 0 && noData}
          </>
        ))}
      {employeesCurrentTab === EmployeesTab.Managers &&
        managers.length > 0 &&
        renderEmployeesList(managers)}
      {employeesCurrentTab === EmployeesTab.ServiceStaff &&
        workers.length > 0 &&
        renderEmployeesList(workers)}
    </div>
  );
}
